import React, { useMemo, Component } from 'react';
import { motion } from 'framer-motion';
import { Clock, CheckCircle, XCircle } from 'lucide-react';
import { AppState, ModuleData } from '../../types';
import { Card } from '../ui/Card';
import * as Icons from 'lucide-react';
// Simple relative time formatter
function formatTimeAgo(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (minutes < 1) return 'Zojuist';
  if (minutes < 60) return `${minutes}m`;
  if (hours < 24) return `${hours}u`;
  if (days === 1) return 'Gisteren';
  if (days < 7) return `${days}d`;
  return `${Math.floor(days / 7)}w`;
}
interface RecentActivityProps {
  state: AppState;
  modules: ModuleData[];
}
export function RecentActivity({
  state,
  modules
}: RecentActivityProps) {
  const activities = useMemo(() => {
    const allAttempts: Array<{
      questionId: string;
      timestamp: number;
      isCorrect: boolean;
      moduleId: string;
      moduleTitle: string;
      moduleIcon: string;
      moduleColor: string;
    }> = [];
    Object.entries(state.questionStates).forEach(([qId, qState]) => {
      let moduleId = '';
      let moduleTitle = '';
      let moduleIcon = 'HelpCircle';
      let moduleColor = 'text-slate-500';
      for (const mod of modules) {
        if (state.progress[mod.id]?.questionsAnswered.includes(qId) || state.progress[mod.id]?.recentQuestions.includes(qId)) {
          moduleId = mod.id;
          moduleTitle = mod.title;
          moduleIcon = mod.icon;
          moduleColor = mod.color;
          break;
        }
      }
      if (!moduleId) return;
      if (qState.lastAttemptDate) {
        allAttempts.push({
          questionId: qId,
          timestamp: qState.lastAttemptDate,
          isCorrect: qState.history[qState.history.length - 1]?.isCorrect ?? false,
          moduleId,
          moduleTitle,
          moduleIcon,
          moduleColor
        });
      }
    });
    return allAttempts.sort((a, b) => b.timestamp - a.timestamp).slice(0, 5);
  }, [state.questionStates, state.progress, modules]);
  if (activities.length === 0) return null;
  return <div className="h-full" data-id="element-484">
      <div className="flex items-center gap-2 mb-4" data-id="element-485">
        <Clock className="w-5 h-5 text-slate-400" data-id="element-486" />
        <h2 className="text-lg font-bold text-slate-900" data-id="element-487">Recent</h2>
      </div>

      <Card className="p-0 overflow-hidden border-slate-200/60 shadow-sm" data-id="element-488">
        <div className="divide-y divide-slate-100" data-id="element-489">
          {activities.map((activity, index) => {
          const IconComponent = (Icons as any)[activity.moduleIcon] || Icons.HelpCircle;
          const bgColorClass = activity.moduleColor.replace('text-', 'bg-').replace('500', '50');
          return <motion.div key={`${activity.questionId}-${activity.timestamp}`} initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: index * 0.05
          }} className="p-3 flex items-center justify-between hover:bg-slate-50 transition-colors" data-id="element-490">
                <div className="flex items-center gap-3 min-w-0" data-id="element-491">
                  <div className={`p-2 rounded-lg flex-shrink-0 ${bgColorClass}`} data-id="element-492">
                    <IconComponent className={`w-4 h-4 ${activity.moduleColor}`} data-id="element-493" />
                  </div>
                  <div className="min-w-0" data-id="element-494">
                    <div className="text-sm font-medium text-slate-900 truncate" data-id="element-495">
                      {activity.moduleTitle}
                    </div>
                    <div className="text-xs text-slate-400" data-id="element-496">
                      {formatTimeAgo(activity.timestamp)}
                    </div>
                  </div>
                </div>

                <div className={`
                  flex items-center justify-center w-6 h-6 rounded-full flex-shrink-0
                  ${activity.isCorrect ? 'bg-emerald-100 text-emerald-600' : 'bg-rose-100 text-rose-600'}
                `} data-id="element-497">
                  {activity.isCorrect ? <CheckCircle className="w-3.5 h-3.5" data-id="element-498" /> : <XCircle className="w-3.5 h-3.5" data-id="element-499" />}
                </div>
              </motion.div>;
        })}
        </div>
      </Card>
    </div>;
}